import { ApolloError, useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';

import { ExportableTableFilters } from '../../../../hooks/useBaseTableFilters/types';
import UseBaseTableFilters from '../../../../hooks/useBaseTableFilters/useBaseTableFilters';
import { ReportFilterStorage } from '../../../../pages/baymax/Report/StandardDelivery/types';
import { StaticFilters } from '../../../../pages/baymax/types/reports';
import { handleGql403 } from '../../../../utils';
import { StorageEntity } from '../../../../utils/storages/storageEntity';
import { prepareSortOptions } from '../../../../utils/table';
import {
  GetStandardDeliveryReportMetricsQuery,
  GetStandardDeliveryReportMetricsQueryVariables,
  StandardDeliveryReportMetrics,
} from '../../types';
import { getMetricOptions } from '../utils';
import { getCampaignIds } from '../utils/utils';
import { GET_STANDARD_DELIVERY_REPORT_METRICS } from './getStandardDeliveryReportMetrics';
import { DynamicFilterArgs, DynamicFiltersFields, DynamicFiltersInput } from './types';

type StandardDeliveryReportMetricsResponse = ExportableTableFilters & {
  data: StandardDeliveryReportMetrics[];
  loading: boolean;
  error?: ApolloError;
  total?: number;
  filters: StaticFilters;
  searchValue: string;
  dynamicFilters: DynamicFiltersInput;
  handleFilter: <T extends DynamicFiltersFields>({ id, value }: DynamicFilterArgs<T>) => void;
  resetAllFilters: () => void;
};

type Props = {
  organizationId: string;
  advertiserId: string;
  reportTemplateId: string;
  storage?: StorageEntity<ReportFilterStorage>;
};

const useStandardDeliveryReportMetrics = ({
  organizationId,
  advertiserId,
  reportTemplateId,
  storage,
}: Props): StandardDeliveryReportMetricsResponse => {
  const {
    handleChangePage,
    handleEntriesPerPage,
    paginationOptions,
    sortBy,
    handleSort,
    handleSearch,
    searchValue,
    setCurrentPage,
    resetFilters,
  } = UseBaseTableFilters({ storage });
  const staticFilters = useMemo(() => storage?.getStorageData('staticFilters') || {}, [storage]);

  const [dynamicFilters, setDynamicFilters] = useState<DynamicFiltersInput | undefined>(
    storage?.getStorageData('dynamicFilters')
  );

  const handleFilter = useCallback(
    ({ id, value }: DynamicFilterArgs<DynamicFiltersFields>): void => {
      setDynamicFilters((state) => {
        storage?.setStorageData('dynamicFilters', { ...state, [id]: value });
        setCurrentPage(0);
        return { ...state, [id]: value };
      });
    },
    [storage]
  );

  const resetAllFilters = useCallback((): void => {
    resetFilters();
    setDynamicFilters({});
    storage?.setStorageData('dynamicFilters', {});
  }, [resetFilters, storage]);

  const { data, loading, error } = useQuery<
    GetStandardDeliveryReportMetricsQuery,
    GetStandardDeliveryReportMetricsQueryVariables
  >(GET_STANDARD_DELIVERY_REPORT_METRICS, {
    variables: {
      metricsOptions: getMetricOptions(reportTemplateId, staticFilters),
      filterOptions: {
        organizationId,
        advertiserId,
        campaignIds: getCampaignIds(staticFilters.campaigns),
        ...dynamicFilters,
      },
      paginationOptions: {
        limit: paginationOptions.entriesPerPage,
        offset: paginationOptions.currentPage * paginationOptions.entriesPerPage,
      },
      sortOptions: prepareSortOptions(sortBy),
      searchTerm: searchValue,
    },
    onError: handleGql403,
    fetchPolicy: 'network-only',
  });

  const standardDeliveryReports: StandardDeliveryReportMetrics[] = useMemo(() => {
    return data?.getStandardDeliveryReportMetrics.edges.map((report) => report.node) || [];
  }, [data]);

  return {
    data: standardDeliveryReports,
    loading: loading,
    error,
    handleChangePage,
    paginationOptions,
    total: data?.getStandardDeliveryReportMetrics.total,
    sortBy,
    handleSort,
    handleSearch,
    handleFilter,
    handleEntriesPerPage,
    filters: staticFilters,
    searchValue: searchValue || '',
    dynamicFilters: dynamicFilters || {},
    resetAllFilters,
  };
};

export default useStandardDeliveryReportMetrics;
